import { defineStore, acceptHMRUpdate } from 'pinia'
import type { ApiError } from '@/api/client'
import { AbsencesService } from '@/api/client'
import { Absence, type TDehydratedAbsence } from '@/classes/Absence'
import { useAuthStore } from '@/stores/AuthStore'
import { DateTime } from 'luxon'
import handleRequest from '@/helpers/handleRequest'

type TState = {
  absences: Absence[]
  lastUpdate: DateTime | null
}

type AbsenceFilter = {
  field: string
  value: Array<string> | Array<number>
}

const initialState: TState = {
  absences: [],
  lastUpdate: null,
}

const useAbsenceStore = defineStore('absenceStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    noData: (state) => {
      return state.lastUpdate === null
    },
  },
  actions: {
    async sync() {
      const data = (await handleRequest(AbsencesService.getAbsences()))?.data
      if (!data) return

      const absences: Absence[] = []
      data.forEach((absence) => {
        absences.push(Absence.createFromServerObject(absence))
      })
      this.absences = absences
      this.lastUpdate = DateTime.now()
    },
    getAbsencesByFilter(filters: AbsenceFilter[]): Absence[] | null {
      let absences = this.$state.absences
      filters.forEach((filter) => {
        switch (filter.field) {
          case 'absencecategory_name':
            absences = absences.filter(
              (absence) =>
                absence.data.category &&
                (filter.value as Array<string>).includes(absence.data.category),
            )
            break
          case 'year':
            absences = absences.filter(
              (absence) =>
                (absence.endsAt &&
                  (filter.value as Array<number>).includes(
                    absence.endsAt.year,
                  )) ||
                (absence.startsAt &&
                  (filter.value as Array<number>).includes(
                    absence.startsAt.year,
                  )),
            )
            break
          case 'approval':
            absences = absences.filter(
              (absence) =>
                absence.data.state &&
                (filter.value as Array<string>).includes(absence.data.state),
            )
            break
        }
      })
      return absences.sort((a, b) => {
        if (!a.startsAt && !b.startsAt) return 0
        if (!a.startsAt) return 1
        if (!b.startsAt) return -1
        return a.startsAt < b.startsAt ? -1 : 1
      })
    },
    async getAbsenceById(id: number): Promise<Absence | null> {
      if (this.$state.absences.length === 0) {
        await this.sync()
      }
      const absence = this.$state.absences.find(
        (absence) => absence.data.id === id,
      )
      return absence || null
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state, (key, value) => {
        if (key === 'absences') {
          return value.map((absenceString: TDehydratedAbsence) =>
            Absence.createFromObject(absenceString),
          )
        } else if (key === 'lastUpdate') {
          return DateTime.fromISO(value)
        } else {
          return value
        }
      })
    },
    add(absence: Absence) {
      if (
        absence.data.id &&
        this.$state.absences.find((a) => a.data.id === absence.data.id)
      ) {
        throw new Error('Absence with this ID already exists')
      }
      this.$state.absences.push(absence)
    },
    async register(absence: Absence, id?: number) {
      let promise
      if (id !== undefined) {
        promise = AbsencesService.updateAbsence(id, absence.getPayload())
      } else {
        promise = AbsencesService.storeAbsence(absence.getPayload())
      }
      await promise
        .then(async () => {
          await this.sync()
        })
        .catch((error: ApiError) => {
          if (error.status === 401) void useAuthStore().logout()
          else throw new Error(error.message)
        })
    },
    async getPossibleSubstitutesForPeriod(
      start: DateTime,
      end: DateTime,
    ): Promise<{ name: string; id: number }[]> {
      const response = await AbsencesService.possibleAbsenceSubstitutes(
        start.startOf('day').toISO(),
        end.endOf('day').toISO(),
      )
      if (response.data) {
        return response.data.map((substitute) => {
          return {
            name: substitute.first_name + ' ' + substitute.last_name,
            id: substitute.id,
          }
        })
      }
      return []
    },
    isAbsentOnDate(date: DateTime, states?: string[]): boolean {
      return this.$state.absences.some((absence) => {
        const startsAt = absence.startsAt
        const endsAt = absence.endsAt
        if (!startsAt || !endsAt) return false

        // If states filter is provided, check if absence state matches
        if (states && states.length > 0) {
          if (!absence.data.state || !states.includes(absence.data.state)) {
            return false
          }
        }

        return date >= startsAt.startOf('day') && date <= endsAt.endOf('day')
      })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAbsenceStore, import.meta.hot))
}

export { useAbsenceStore }
